import React, { useState } from 'react';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';

import { Imagem } from 'components/images';
import { ContentModal } from 'components/modal';
import { SubTitle } from 'components/text';
import { UploadButton } from 'components/upload/UploadButton';
import { useBeforeUnload } from 'hooks/useBeforeUnload';
import { useMimeTypes } from 'hooks/useMimeTypes';

interface UploadItem {
    file: File | any;
    status: 'pending' | 'uploading' | 'done' | string;
}

interface MultiUploadModalProps {
    setOpen: (open: boolean) => void;
    open: boolean;
    loading: boolean;
    accept: any;
    callback?: any;
}

const getImagePreview = (selectedFile: any) => {
    const type = selectedFile.type.split('/')[0];

    let previewImg = null;

    if (type === 'image') {
        previewImg = (
            <Box>
                <Imagem src={URL.createObjectURL(selectedFile)} style={{ maxHeight: 250, margin: '0 auto' }} />
            </Box>
        );
    } else {
        previewImg = (
            <Box
                sx={{
                    backgroundColor: 'grey.200',
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    fontSize: '0.7rem',
                }}
            >
                <InsertDriveFileOutlinedIcon sx={{ display: 'block', mb: 0.5 }} />

                {selectedFile.name.split('.').at(-1)}
            </Box>
        );
    }

    return previewImg;
};

/**
 * Componente de Upload de Múltiplos Arquivos,
 */
export function MultiUploadModal({ setOpen, open, accept, callback, loading }: MultiUploadModalProps) {
    const [uploadQueue, setUploadQueue] = useState<UploadItem[]>([]);
    const { getFileNameWithoutExtension } = useMimeTypes();

    useBeforeUnload({ preventUnload: loading });

    const handleFileChange = (selectedFiles: File[]): void => {
        const newUploadItems: UploadItem[] = selectedFiles.map((file) => ({
            file,
            status: 'pending',
        }));

        setUploadQueue((prevUploadQueue) => [...prevUploadQueue, ...newUploadItems]);
    };

    const handleUpload = (): void => {
        const filesWithTitles = uploadQueue.map((uploadItem) => ({
            file: uploadItem.file,
            dsTitulo: getFileNameWithoutExtension(uploadItem.file.name),
        }));

        callback(filesWithTitles).then(() => setUploadQueue([]));
    };

    const handleRemoveFile = (fileIndex: number): void => {
        setUploadQueue(uploadQueue.filter((item, index) => index !== fileIndex));
    };

    return (
        <ContentModal
            open={open}
            setOpen={setOpen}
            closeButton={!loading}
            disableBackdropClick={loading}
            onClose={() => setUploadQueue([])}
            sx={{
                width: 400,
                borderRadius: 1,
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <SubTitle label="Adicionar Arquivos" />

                <UploadButton label="Selecionar arquivos" onChangeMultiple={handleFileChange} multiple accept={accept} disabled={loading} />

                {uploadQueue.map((uploadItem, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', p: 1, mt: 2, borderRadius: 1, boxShadow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mr: 1.5, flex: '0 0 80px' }}>
                            {uploadItem.file && getImagePreview(uploadItem.file)}
                        </Box>

                        <Box sx={{ flex: '2', flexDirection: 'column', overflow: 'hidden' }}>
                            <Typography variant="body1" sx={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                                Nome do arquivo: {uploadItem.file.name}
                            </Typography>
                        </Box>

                        <Box sx={{ flex: '0 0 50px', justifyContent: 'center', alignItems: 'center' }}>
                            <IconButton sx={{ width: 60 }} disabled={loading} onClick={() => handleRemoveFile(index)} color="primary">
                                <DeleteOutlineIcon fontSize="large" />
                            </IconButton>
                        </Box>
                    </Box>
                ))}

                <Stack spacing={2} justifyContent="flex-end" direction="row" sx={{ mt: 3 }}>
                    <Button variant="outlined" disabled={loading} onClick={() => setOpen(false)}>
                        Cancelar
                    </Button>

                    <Button variant="contained" loading={loading} onClick={handleUpload} disabled={!uploadQueue.length}>
                        Adicionar Arquivo
                    </Button>
                </Stack>
            </Box>
        </ContentModal>
    );
}
